'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');

const SERVICE_ICONS = { AEPS: '🔒', DMT: '🏦', UPI: '⬛', BBPS: '⚡', MATM: '🏧', POS: '💳' };
const DEFAULT_INTERFACE_BY_SERVICE = { UPI: 'ISG', AEPS: 'NSDL', DMT: 'NSDL' };
const STEP_TITLES = [
  'Account Setup', 'Merchant Profile', 'Business Details', 'Store Location',
  'Address Details', 'Beneficiary & Review', 'Select Services',
];

function getLocation() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) }),
      () => resolve(null),
      { timeout: 8000, maximumAge: 60000 },
    );
  });
}

/**
 * MerchantSignupWidget — the full self-service "Become a Bucksbox Merchant"
 * wizard: account credentials -> merchant profile -> business details ->
 * store location -> address details -> beneficiary/review -> optional
 * service selection -> register. Mirrors webpayment's pages/register/index.vue
 * step-for-step (same fields, same validation, same submit payload shape),
 * ported onto client.registration (RegistrationModule) — authenticated the
 * same way as every other module in this SDK, with the *onboarding vendor's*
 * own clientId/key (issued at that vendor's own registration), not the new
 * merchant's — the merchant has none yet, that's what this call creates.
 * paymentSystem resolves the new merchant's vendor from that same clientId
 * server-side (server.js's preHandler hook), so nothing extra needs to be
 * passed to link it to the calling vendor.
 *
 * const client = new BucksBoxClient({ environment: 'staging', clientId, key }); // the vendor's own credentials
 * const widget = new MerchantSignupWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-signup').start();
 *
 * onSuccess receives only { statusCode, message, existing, merchantId, clientId, key } —
 * never the raw user/merchant rows paymentSystem's /register response carries (password
 * hash, internal columns, etc). merchantId/clientId/key are exactly what's needed to
 * construct a BucksBoxClient for the newly-registered merchant going forward.
 *
 * opts.mobileNumber (optional) — pre-fills and locks the mobile number field,
 * for callers arriving with an already-OTP-verified number (mirrors payapp's
 * register/index.vue, which reads it from router state as a disabled field
 * rather than asking the merchant to re-type it — see MerchantOnboardingWidget,
 * which hands off here with exactly this).
 */
class MerchantSignupWidget extends BaseWidget {
  start() {
    this._p = {
      step: 1,
      mobile_no: this.opts.mobileNumber || '',
      gender: 'M',
      std_code: '091',
      lead_source: 'BUCKSBOX',
      ckyc: true,
      interface: 'MOS',
      sameAsOfficialResidential: false,
      sameAsOfficialVisitor: false,
      serviceSelections: {},
    };
    this._businessTypes = null;
    this._turnoverRanges = null;
    this._serviceCatalog = null;
    this.screenStep();
    return this;
  }

  // ── Shared chrome ───────────────────────────────────────────────────

  stepSubtitle() {
    return `Step ${this._p.step} of 7 · ${STEP_TITLES[this._p.step - 1]}`;
  }

  screenStep(errorMsg) {
    const renderers = [
      () => this.screen1Account(errorMsg),
      () => this.screen2Profile(errorMsg),
      () => this.screen3Business(errorMsg),
      () => this.screen4Location(errorMsg),
      () => this.screen5Address(errorMsg),
      () => this.screen6Review(errorMsg),
      () => this.screen7Services(errorMsg),
    ];
    renderers[this._p.step - 1]();
  }

  setStepHeader() {
    this.setHeader({
      title: 'Become a Merchant',
      subtitle: this.stepSubtitle(),
      showBack: this._p.step > 1,
      onBack: () => { this._p.step--; this.screenStep(); },
    });
  }

  selectField({ label, options, value, placeholder, onChange }) {
    const select = h(
      'select',
      { class: 'bbx-input', onChange: (e) => onChange && onChange(e.target.value) },
      [
        placeholder ? h('option', { value: '', text: placeholder }) : null,
        ...options.map((o) => h('option', { value: String(o.value), text: o.label })),
      ],
    );
    if (value != null && value !== '') select.value = String(value);
    return { node: h('div', { class: 'bbx-field' }, [label ? h('label', { text: label }) : null, select]), select };
  }

  checkboxField(label, checked, onChange) {
    return h('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: BX.ink, margin: '4px 0 14px', cursor: 'pointer' } }, [
      h('input', { type: 'checkbox', checked, onChange: (e) => onChange(e.target.checked) }),
      h('span', { text: label }),
    ]);
  }

  /**
   * Debounced search input -> a directly-clickable list of result rows
   * (same `.bbx-select-item` row style DmtWidget/AepsWidget already use for
   * pick-one-from-a-list, just stacked instead of gridded) — click a row to
   * pick it straight away, no separate "open dropdown, then choose" step.
   * @param {(x: object) => {title: string, subtitle?: string}} renderRow
   */
  searchSelectField({ label, placeholder, search, renderRow, onPick }) {
    const MAX_SHOWN = 8;
    let debounceId = null;
    let requestSeq = 0;
    const resultsWrap = h('div', { style: { marginTop: '8px' } });
    const input = h('input', {
      class: 'bbx-input',
      placeholder,
      onInput: (e) => {
        clearTimeout(debounceId);
        const q = e.target.value.trim();
        if (q.length < 2) { resultsWrap.replaceChildren(); return; }
        const seq = ++requestSeq;
        debounceId = setTimeout(async () => {
          resultsWrap.replaceChildren(h('p', { class: 'bbx-hint', text: 'Searching…' }));
          let list;
          try {
            const res = await search(q);
            list = res?.data || [];
          } catch (err) {
            if (seq === requestSeq) resultsWrap.replaceChildren(h('p', { class: 'bbx-hint', text: 'Search failed — try again' }));
            return;
          }
          if (seq !== requestSeq) return; // a newer keystroke's search already landed
          resultsWrap.replaceChildren();
          if (!list.length) { resultsWrap.appendChild(h('p', { class: 'bbx-hint', text: 'No matches found' })); return; }
          list.slice(0, MAX_SHOWN).forEach((x) => {
            const { title, subtitle } = renderRow(x);
            resultsWrap.appendChild(
              h('div', {
                class: 'bbx-select-item',
                style: { textAlign: 'left', marginBottom: '6px', padding: '10px 12px' },
                onClick: () => {
                  onPick(x);
                  input.value = '';
                  resultsWrap.replaceChildren();
                },
              }, [
                h('div', { style: { fontWeight: '800', fontSize: '13px' }, text: title }),
                subtitle ? h('div', { class: 'bbx-hint', style: { margin: '2px 0 0' }, text: subtitle }) : null,
              ]),
            );
          });
          if (list.length > MAX_SHOWN) {
            resultsWrap.appendChild(h('p', { class: 'bbx-hint', text: `${list.length - MAX_SHOWN} more match — refine your search to narrow it down` }));
          }
        }, 300);
      },
    });
    return h('div', { class: 'bbx-field' }, [label ? h('label', { text: label }) : null, input, resultsWrap]);
  }

  // ── Step 1: Account Setup ───────────────────────────────────────────

  screen1Account(errorMsg) {
    this.setStepHeader();
    const emailField = this.field({ label: 'Email Address', type: 'email', placeholder: 'merchant@email.com', value: this._p.email || '', onInput: (v) => { this._p.email = v.trim(); } });
    const mobileLocked = !!this.opts.mobileNumber;
    const mobileField = this.field({ label: mobileLocked ? 'Mobile Number (verified)' : 'Mobile Number', inputmode: 'numeric', maxlength: '10', placeholder: '10-digit mobile', value: this._p.mobile_no || '', onInput: (v) => { this._p.mobile_no = v.replace(/\D/g, '').slice(0, 10); } });
    if (mobileLocked) mobileField.input.disabled = true;
    const passField = this.field({ label: 'Password', type: 'password', placeholder: 'Min. 8 characters', value: this._p.password || '', onInput: (v) => { this._p.password = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        emailField.node, mobileField.node, passField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Continue',
          onClick: () => {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this._p.email || '')) return errorSlot.replaceChildren(this.banner('error', 'Enter a valid email address'));
            if (!/^\d{10}$/.test(this._p.mobile_no || '')) return errorSlot.replaceChildren(this.banner('error', 'Enter a valid 10-digit mobile number'));
            if (!this._p.password || this._p.password.length < 8) return errorSlot.replaceChildren(this.banner('error', 'Password must be at least 8 characters'));
            this._p.step = 2;
            this.screenStep();
          },
        }),
      ]),
    );
  }

  // ── Step 2: Merchant Profile ────────────────────────────────────────

  screen2Profile(errorMsg) {
    this.setStepHeader();
    const legalField = this.field({ label: 'Merchant Legal Name', placeholder: 'As per legal documents', value: this._p.legal_name || '', onInput: (v) => { this._p.legal_name = v; } });
    const bizField = this.field({ label: 'Business / Store Name', placeholder: 'Display name', value: this._p.business_name || '', onInput: (v) => { this._p.business_name = v; } });
    const dbaField = this.field({ label: 'DBA / Trade Name (optional)', placeholder: 'Trade / Brand name', value: this._p.dba_name || '', onInput: (v) => { this._p.dba_name = v; } });
    const dobField = this.field({ label: 'Date of Birth', type: 'date', value: this._p.dob || '', onInput: (v) => { this._p.dob = v; } });
    const genderField = this.selectField({ label: 'Gender', value: this._p.gender, options: [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }, { value: 'O', label: 'Other' }], onChange: (v) => { this._p.gender = v; } });
    const primaryEmailField = this.field({ label: 'Primary Email (optional)', type: 'email', placeholder: 'Defaults to login email', value: this._p.primary_email_id || '', onInput: (v) => { this._p.primary_email_id = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        legalField.node, bizField.node, dbaField.node, dobField.node, genderField.node, primaryEmailField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Continue',
          onClick: () => {
            if (!this._p.legal_name || this._p.legal_name.length < 3) return errorSlot.replaceChildren(this.banner('error', 'Legal name needs at least 3 characters'));
            if (!this._p.business_name || this._p.business_name.length < 3) return errorSlot.replaceChildren(this.banner('error', 'Business name needs at least 3 characters'));
            if (!this._p.dob) return errorSlot.replaceChildren(this.banner('error', 'Date of birth is required'));
            this._p.step = 3;
            this.screenStep();
          },
        }),
      ]),
    );
  }

  // ── Step 3: Business Details ────────────────────────────────────────

  async screen3Business(errorMsg) {
    this.setStepHeader();
    this.renderLoading('Loading business types…');
    if (!this._businessTypes) {
      try { this._businessTypes = (await this.client.registration.getBusinessTypes())?.data || []; } catch (e) { this._businessTypes = []; }
    }
    if (!this._turnoverRanges) {
      try { this._turnoverRanges = (await this.client.registration.getTurnoverRanges())?.data || []; } catch (e) { this._turnoverRanges = []; }
    }

    const typeField = this.selectField({
      label: 'Business Type', placeholder: 'Choose business type', value: this._p.business_type,
      options: this._businessTypes.map((t) => ({ value: t.value, label: t.label })),
      onChange: (v) => { this._p.business_type = v; },
    });

    const mccField = this.searchSelectField({
      label: 'Nature of Business (MCC)', placeholder: 'Search e.g. Apparel',
      search: (q) => this.client.registration.searchMcc(q),
      renderRow: (x) => ({ title: x.descr }),
      onPick: (x) => { this._p.mcc = String(x.id); this._p.nature_of_business = x.descr; mccPicked.textContent = `Selected: ${x.descr}`; },
    });
    const mccPicked = h('p', { class: 'bbx-hint', text: this._p.nature_of_business ? `Selected: ${this._p.nature_of_business}` : '' });

    const turnoverField = this.selectField({
      label: 'Annual Turnover', placeholder: 'Select turnover range', value: this._p.annual_turn_over,
      options: this._turnoverRanges.map((t) => ({ value: t.value, label: t.label || t.value })),
      onChange: (v) => { this._p.annual_turn_over = v; },
    });

    const websiteField = this.field({ label: 'Website (optional)', placeholder: 'www.yourbusiness.com', value: this._p.website || '', onInput: (v) => { this._p.website = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        typeField.node, mccField, mccPicked, turnoverField.node, websiteField.node,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Continue',
          onClick: () => {
            if (!this._p.business_type) return errorSlot.replaceChildren(this.banner('error', 'Please select a business type'));
            if (!this._p.mcc) return errorSlot.replaceChildren(this.banner('error', 'Please select nature of business'));
            if (!this._p.annual_turn_over) return errorSlot.replaceChildren(this.banner('error', 'Please select annual turnover'));
            this._p.step = 4;
            this.screenStep();
          },
        }),
      ]),
    );
  }

  // ── Step 4: Store Location ──────────────────────────────────────────

  async screen4Location() {
    this.setStepHeader();
    if (!this._p.lat && !this._p.long) {
      const loc = await getLocation();
      if (loc) { this._p.lat = loc.lat; this._p.long = loc.lng; }
    }

    const latField = this.field({ label: 'Latitude', placeholder: '0.00000', value: this._p.lat || '', onInput: (v) => { this._p.lat = v; } });
    const longField = this.field({ label: 'Longitude', placeholder: '0.00000', value: this._p.long || '', onInput: (v) => { this._p.long = v; } });

    this.showScreen(
      h('div', {}, [
        h('p', { class: 'bbx-hint', text: this._p.lat ? 'Location detected automatically — edit if needed.' : 'Could not detect location automatically — enter it manually or allow location access and retry.' }),
        latField.node, longField.node,
        h('button', {
          class: 'bbx-btn bbx-btn-secondary', style: { marginBottom: '14px' }, text: 'Use my current location',
          onClick: async (e) => {
            e.target.disabled = true;
            const loc = await getLocation();
            e.target.disabled = false;
            if (loc) { this._p.lat = loc.lat; this._p.long = loc.lng; this.screenStep(); }
          },
        }),
        h('button', { class: 'bbx-btn', text: 'Continue', onClick: () => { this._p.step = 5; this.screenStep(); } }),
      ]),
    );
  }

  // ── Step 5: Address Details ─────────────────────────────────────────

  addressBlock(fields) {
    const p = this._p;
    const pinField = this.searchSelectField({
      label: 'Search Pincode', placeholder: 'Search pincode',
      search: (q) => this.client.registration.searchPincode(q),
      renderRow: (x) => ({ title: x.pincode, subtitle: [x.city, x.district, x.statename].filter(Boolean).join(', ') }),
      onPick: (x) => {
        p[fields.pincode] = x.pincode; p[fields.city] = x.city; p[fields.state] = x.statename;
        pinSummary.textContent = `${x.pincode} — ${x.city}, ${x.statename}`;
      },
    });
    const pinSummary = h('p', { class: 'bbx-hint', text: p[fields.pincode] ? `${p[fields.pincode]} — ${p[fields.city] || ''}, ${p[fields.state] || ''}` : '' });

    const rows = [
      this.field({ label: 'Door / Building', placeholder: 'Flat / Shop / Building', value: p[fields.line0] || '', onInput: (v) => { p[fields.line0] = v; } }).node,
      this.field({ label: 'Street Address 1', value: p[fields.line1] || '', onInput: (v) => { p[fields.line1] = v; } }).node,
      this.field({ label: 'Street Address 2', value: p[fields.line2] || '', onInput: (v) => { p[fields.line2] = v; } }).node,
    ];
    if (fields.line3) {
      rows.push(this.field({ label: fields.line3.label, placeholder: 'Landmark (optional)', value: p[fields.line3.key] || '', onInput: (v) => { p[fields.line3.key] = v; } }).node);
    }
    if (fields.mobile) {
      rows.push(this.field({ label: fields.mobile.label, inputmode: 'numeric', maxlength: '10', value: p[fields.mobile.key] || '', onInput: (v) => { p[fields.mobile.key] = v.replace(/\D/g, '').slice(0, 10); } }).node);
    }
    rows.push(pinField, pinSummary);
    return rows;
  }

  screen5Address(errorMsg) {
    this.setStepHeader();
    const p = this._p;
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    const official = this.addressBlock({ line0: 'official_address', line1: 'address1', line2: 'address2', line3: { key: 'address3', label: 'Landmark (optional)' }, pincode: 'pincode', city: 'city', state: 'state' });
    const phoneField = this.field({ label: 'Contact Number (optional)', inputmode: 'numeric', maxlength: '10', value: p.phone || '', onInput: (v) => { p.phone = v.replace(/\D/g, '').slice(0, 10); } });

    const resToggle = this.checkboxField('Residential address is same as official', p.sameAsOfficialResidential, (checked) => { p.sameAsOfficialResidential = checked; this.screen5Address(); });
    const resBlock = p.sameAsOfficialResidential ? null : h('div', {}, this.addressBlock({ line0: 'residential_address', line1: 'res_address1', line2: 'res_address2', mobile: { key: 'res_mobile', label: 'Residential Mobile (optional)' }, pincode: 'res_pincode', city: 'res_city', state: 'res_state' }));

    const vToggle = this.checkboxField('Visitor address is same as official', p.sameAsOfficialVisitor, (checked) => { p.sameAsOfficialVisitor = checked; this.screen5Address(); });
    const vBlock = p.sameAsOfficialVisitor ? null : h('div', {}, this.addressBlock({ line0: 'vister_address', line1: 'v_address1', line2: 'v_address2', mobile: { key: 'v_mobile', label: 'Visitor Mobile (optional)' }, pincode: 'v_pincode', city: 'v_city', state: 'v_state' }));

    this.showScreen(
      h('div', {}, [
        h('p', { class: 'bbx-hint', text: 'OFFICIAL / REGISTERED ADDRESS' }),
        ...official, phoneField.node,
        resToggle, resBlock,
        vToggle, vBlock,
        errorSlot,
        h('button', {
          class: 'bbx-btn', text: 'Continue',
          onClick: () => {
            const need = (v, msg) => { if (!v) { errorSlot.replaceChildren(this.banner('error', msg)); return false; } return true; };
            if (!need(p.official_address, 'Official address is required')) return;
            if (!need(p.address1, 'Street address line 1 is required')) return;
            if (!need(p.address2, 'Street address line 2 is required')) return;
            if (!need(p.pincode, 'Official pincode is required')) return;
            if (!need(p.city, 'Official city is required')) return;
            if (!need(p.state, 'Official state is required')) return;
            if (!p.sameAsOfficialResidential) {
              if (!need(p.residential_address, 'Residential address is required')) return;
              if (!need(p.res_address1, 'Residential street address 1 is required')) return;
              if (!need(p.res_address2, 'Residential street address 2 is required')) return;
              if (!need(p.res_pincode, 'Residential pincode is required')) return;
              if (!need(p.res_city, 'Residential city is required')) return;
              if (!need(p.res_state, 'Residential state is required')) return;
            }
            if (!p.sameAsOfficialVisitor) {
              if (!need(p.vister_address, 'Visitor address is required')) return;
              if (!need(p.v_address1, 'Visitor street address 1 is required')) return;
              if (!need(p.v_address2, 'Visitor street address 2 is required')) return;
              if (!need(p.v_pincode, 'Visitor pincode is required')) return;
              if (!need(p.v_city, 'Visitor city is required')) return;
              if (!need(p.v_state, 'Visitor state is required')) return;
            }
            this._p.step = 6;
            this.screenStep();
          },
        }),
      ]),
    );
  }

  // ── Step 6: Beneficiary & Review ────────────────────────────────────

  screen6Review() {
    this.setStepHeader();
    const p = this._p;
    const benEmail = this.field({ label: 'Beneficiary Email (optional)', type: 'email', placeholder: 'beneficiary@email.com', value: p.beneficiary_email || '', onInput: (v) => { p.beneficiary_email = v; } });
    const benMobile = this.field({ label: 'Beneficiary Mobile (optional)', inputmode: 'numeric', maxlength: '10', value: p.beneficiary_mobile || '', onInput: (v) => { p.beneficiary_mobile = v.replace(/\D/g, '').slice(0, 10); } });

    const businessTypeLabel = (this._businessTypes || []).find((t) => t.value === p.business_type)?.label || p.business_type || '—';

    this.showScreen(
      h('div', {}, [
        benEmail.node, benMobile.node,
        h('div', { class: 'bbx-card' }, [
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Email' }), h('span', { class: 'bbx-row-v', text: p.email || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Mobile' }), h('span', { class: 'bbx-row-v', text: p.mobile_no || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Legal Name' }), h('span', { class: 'bbx-row-v', text: p.legal_name || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Business' }), h('span', { class: 'bbx-row-v', text: p.business_name || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Type' }), h('span', { class: 'bbx-row-v', text: businessTypeLabel })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'MCC' }), h('span', { class: 'bbx-row-v', text: p.nature_of_business || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'City' }), h('span', { class: 'bbx-row-v', text: p.city || '—' })]),
          h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Pincode' }), h('span', { class: 'bbx-row-v', text: p.pincode || '—' })]),
        ]),
        h('p', { class: 'bbx-hint', text: 'By continuing, you agree to the Terms of Service and Privacy Policy.' }),
        h('button', { class: 'bbx-btn', text: 'Continue', onClick: () => { this._p.step = 7; this.screenStep(); } }),
      ]),
    );
  }

  // ── Step 7: Select Services ─────────────────────────────────────────

  async screen7Services(errorMsg) {
    this.setStepHeader();
    this.renderLoading('Loading available services…');
    if (!this._serviceCatalog) {
      try { this._serviceCatalog = (await this.client.registration.getServiceCatalog())?.data || []; } catch (e) { this._serviceCatalog = []; }
    }

    const toggle = (svc) => {
      const cur = this._p.serviceSelections[svc.id];
      if (cur?.selected) {
        this._p.serviceSelections[svc.id] = { selected: false, service: svc.service, interfaceName: '' };
      } else {
        const preferredName = DEFAULT_INTERFACE_BY_SERVICE[svc.service];
        const preferred = preferredName && (svc.interfaces || []).find((i) => i.interface === preferredName);
        const fallback = preferred || svc.interfaces?.[0] || null;
        this._p.serviceSelections[svc.id] = { selected: true, service: svc.service, interfaceName: fallback?.interface || '' };
      }
      this.screenStep();
    };

    const grid = this._serviceCatalog.length
      ? h('div', { class: 'bbx-select-grid' }, this._serviceCatalog.map((svc) =>
          h('div', { class: `bbx-select-item${this._p.serviceSelections[svc.id]?.selected ? ' bbx-selected' : ''}`, onClick: () => toggle(svc) }, [
            h('div', { class: 'bbx-sel-icon', text: SERVICE_ICONS[svc.service] || '▫' }),
            h('div', { class: 'bbx-sel-label', text: svc.service }),
          ]),
        ))
      : h('p', { class: 'bbx-hint', text: 'No services are available to select right now — you can skip this step.' });

    const selectedCount = Object.values(this._p.serviceSelections).filter((s) => s.selected).length;
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);

    this.showScreen(
      h('div', {}, [
        h('p', { class: 'bbx-hint', text: 'Choose the services you\'ll offer, or skip and add these later. A default provider is assigned automatically for each.' }),
        grid,
        selectedCount ? h('p', { class: 'bbx-hint', text: `${selectedCount} service${selectedCount > 1 ? 's' : ''} selected.` }) : null,
        errorSlot,
        h('button', { class: 'bbx-btn', text: 'Register', onClick: () => this.submit() }),
      ]),
    );
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async submit() {
    const p = this._p;
    this.showOverlay('Creating your merchant account…');

    const services = Object.values(p.serviceSelections).filter((s) => s.selected && s.interfaceName).map((s) => ({ service: s.service, interfaceName: s.interfaceName }));

    // paymentSystem's merchant.service.js#CreateMerchantInfo destructures
    // these straight off req.body with no server-side defaulting, and
    // Prisma treats an `undefined` value the same as the key being absent
    // — so any field its schema requires (address3, res_mobile,
    // res_phone_number, v_address3, ... — not all of which have a visible
    // input; webpayment's Vue form covers this by initializing its whole
    // `form` object to "" up front, even for fields with no UI control) must
    // still be sent as at least '' rather than left undefined, or
    // registration fails with a Prisma "Argument X is missing" error.
    const str = (v) => (v == null ? '' : String(v));

    const payload = {
      email: p.email,
      password: p.password,
      mobile_no: p.mobile_no,
      interface: p.interface,
      legal_name: p.legal_name,
      business_name: p.business_name,
      dba_name: str(p.dba_name),
      dob: p.dob,
      gender: p.gender,
      primary_email_id: p.primary_email_id || p.email,
      primary_mobile: p.primary_mobile || String(p.mobile_no),
      business_type: p.business_type,
      mcc: p.mcc,
      nature_of_business: p.nature_of_business,
      annual_turn_over: p.annual_turn_over,
      website: str(p.website),
      location: { latitude: Number(p.lat) || 0, longitude: Number(p.long) || 0 },
      official_address: str(p.official_address),
      address1: str(p.address1),
      address2: str(p.address2),
      address3: str(p.address3),
      phone: str(p.phone),
      city: str(p.city),
      state: str(p.state),
      pincode: str(p.pincode),
      residential_address: str(p.sameAsOfficialResidential ? p.official_address : p.residential_address),
      res_address1: str(p.sameAsOfficialResidential ? p.address1 : p.res_address1),
      res_address2: str(p.sameAsOfficialResidential ? p.address2 : p.res_address2),
      res_address3: str(p.sameAsOfficialResidential ? p.address3 : p.res_address3),
      res_mobile: str(p.res_mobile),
      res_phone_number: str(p.res_phone_number),
      res_city: str(p.sameAsOfficialResidential ? p.city : p.res_city),
      res_state: str(p.sameAsOfficialResidential ? p.state : p.res_state),
      res_pincode: str(p.sameAsOfficialResidential ? p.pincode : p.res_pincode),
      vister_address: str(p.sameAsOfficialVisitor ? p.official_address : p.vister_address),
      v_address1: str(p.sameAsOfficialVisitor ? p.address1 : p.v_address1),
      v_address2: str(p.sameAsOfficialVisitor ? p.address2 : p.v_address2),
      v_address3: str(p.sameAsOfficialVisitor ? p.address3 : p.v_address3),
      v_mobile: str(p.v_mobile),
      v_phone_number: str(p.v_phone_number),
      v_city: str(p.sameAsOfficialVisitor ? p.city : p.v_city),
      v_state: str(p.sameAsOfficialVisitor ? p.state : p.v_state),
      v_pincode: str(p.sameAsOfficialVisitor ? p.pincode : p.v_pincode),
      beneficiary_email: str(p.beneficiary_email),
      beneficiary_mobile: str(p.beneficiary_mobile),
      std_code: p.std_code,
      lead_source: p.lead_source,
      lg_code: str(p.lg_code),
      ckyc: p.ckyc,
      services,
    };

    try {
      const res = await this.client.registration.register(payload);
      this.hideOverlay();
      if (res?.statusCode === '00') {
        this.screenResult(res);
      } else {
        // Stay on this step (7 — Select Services) with the error shown
        // inline and every field the merchant already filled in still
        // intact — don't send them back to step 1 to re-enter everything.
        this.fail(res, res?.message);
        this.screenStep(res?.message || 'Registration failed');
      }
    } catch (err) {
      this.hideOverlay();
      this.fail(err);
      this.screenStep(this.errorMessage(err, 'Registration failed'));
    }
  }

  screenResult(res) {
    this.setHeader({ title: 'Become a Merchant', showBack: false });
    const existing = !!res?.data?.existing;
    const mid = res?.data?.mid; // human-readable code (e.g. "MID...") — display only

    // paymentSystem's /register response carries the full raw user/merchant
    // rows (bcrypt password hash, apiKey, SaltAESKey, every internal column)
    // under data.user/data.merchant — never hand that wholesale back to the
    // host page. Only what it actually needs to proceed: the merchant's real
    // id (merchant.id, not the mid code above — this is what merchantId
    // means everywhere else in this SDK, e.g. BucksBoxClient's config) and
    // the new merchant's own credentials to start calling the SDK as them.
    const payload = {
      statusCode: res?.statusCode,
      message: res?.message,
      existing,
      merchantId: res?.data?.merchant?.id,
      clientId: res?.data?.user?.apiKey,
      key: res?.data?.user?.SaltAESKey,
    };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: BX.success2 }, text: '✓' }),
        h('p', { class: 'bbx-status-title', text: existing ? 'Account Already Exists' : 'Registration Successful' }),
        h('p', { class: 'bbx-status-sub', text: existing ? 'A merchant account for these details already exists — log in to continue onboarding.' : (res?.message || 'Your merchant account has been created. Log in to continue onboarding.') }),
        mid ? h('div', { class: 'bbx-card' }, [h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Merchant ID' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(mid) })])]) : null,
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { this.succeed(payload); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { MerchantSignupWidget };
